import Swiper from "swiper";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

/**
 * Represents an optional value that may or may not be present.
 *
 * @template A
 */
export const Maybe = class {
	/** @type {boolean} */
	#is_some;
	/** @type {A | undefined} */
	#item;

	/**
	 * Creates a Maybe instance with the given state.
	 *
	 * @param {{ is_some: boolean, item?: A }} state The state of the Maybe instance.
	 */
	constructor(state) {
		this.#is_some = state.is_some;
		this.#item = state.item;
	}

	/**
	 * Creates a Maybe instance representing a value.
	 *
	 * @param {A} item The value to be wrapped.
	 * @return {Maybe<A>} The `some` variant containing the value.
	 */
	static some = (item) => new Maybe({ is_some: true, item });

	/**
	 * Creates a Maybe instance representing no value.
	 *
	 * @return {Maybe<A>} The `none` variant.
	 */
	static none = () => new Maybe({ is_some: false, item: undefined });

	/**
	 * Maps the value of the Maybe instance to a new Maybe instance with a different type.
	 *
	 * @template B
	 * @param {function(A): B} fn The function to apply to the value in the Maybe instance.
	 * @return {Maybe<B>} A Maybe instance with the result of applying `fn` to the value if it is `some`, otherwise `none`.
	 */
	map = (fn) => (this.#is_some ? Maybe.some(fn(this.#item)) : Maybe.none());
};

/**
 * Initialise swiper instances when the window width is < the breakpoint, else destroys them.
 * @param {SwiperOptions | undefined} options Swiper instance options.
 * @param {number} breakpoint Window width to use as breakpoint.
 * @param {CSSSelector | HTMLElement} container Where swiper applies to.
 */
const swiper_worker = (options) => (breakpoint) => (container) => {
	const media_query = `(min-width: ${breakpoint}px)`;
	const media_query_list = window.matchMedia(media_query);
	const make_swiper = (options) => (container) =>
		new Swiper(container, options);
	let maybe_swipers = Maybe.none();
	const listener = (media_query_list) => {
		maybe_swipers.map((swipers) => {
			swipers.forEach((swiper) => {
				swiper.destroy();
			});
		});
		if (window.innerWidth < breakpoint) {
			maybe_swipers = Maybe.some(make_swiper(options)(container));
		}
	};
	listener(media_query_list);
	media_query_list.addEventListener("change", listener);
};

const breakpoint = 1024;
const container = ".cards-swiper";
const options = {
	autoHeight: true,
	breakpoints: {
		1024: {
			autoHeight: false,
			slidesPerView: 0,
			allowSlideNext: false,
			allowSlidePrev: false,
		},
	},
	loop: true,
	modules: [Pagination],
	pagination: {
		el: ".swiper-pagination",
	},
	watchOverflow: true,
};
swiper_worker(options)(breakpoint)(container);
